import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { GfFlyerItemDto, Coordinate } from "@swagger/typescript-fetch-goflyer";
import { CoordinateChange } from "../types";
import { COORDINATE_BOX_CONSTANTS } from "../constants";

interface UseCoordinateManagerProps {
  flyerItems: GfFlyerItemDto[];
  currentPage: number;
  imageBounds: { width: number; height: number };
}

interface UseCoordinateManagerReturn {
  getItemsForCurrentPage: () => GfFlyerItemDto[];
  updateCoordinate: (
    itemId: string,
    newCoordinate: Coordinate,
    isAIExtraction?: boolean,
  ) => void;
  getCoordinate: (itemId: string) => Coordinate | undefined;
  getModifiedCoordinates: () => CoordinateChange[];
  getAIExtractionCoordinates: () => CoordinateChange[];
  getRegularCoordinates: () => CoordinateChange[];
  isAIExtraction: (itemId: string) => boolean;
  isDirty: boolean;
  resetChanges: () => string[];
  resetCoordinate: (itemId: string) => void;
}

/**
 * Custom hook for managing coordinate modifications in the flyer viewer.
 *
 * This hook tracks coordinate changes for flyer items, maintaining both original
 * and modified coordinate states. It provides methods to update, retrieve, and reset
 * coordinates while ensuring all changes stay within image bounds. Uses refs to prevent
 * stale closures and avoid unnecessary re-renders. Automatically cleans up memory on unmount.
 *
 * Key features:
 * - Tracks modified coordinates separate from original data
 * - Validates coordinates to stay within image bounds
 * - Provides dirty state tracking for unsaved changes
 * - Supports resetting individual or all changes
 * - Prevents memory leaks with cleanup on unmount
 *
 * @param props - Hook configuration
 * @param props.flyerItems - Array of all flyer items with their coordinates
 * @param props.currentPage - The current page number being viewed (1-based)
 * @param props.imageBounds - Dimensions of the image to constrain coordinates within
 * @returns Object containing coordinate management functions and state
 */
export const useCoordinateManager = ({
  flyerItems,
  currentPage,
  imageBounds,
}: UseCoordinateManagerProps): UseCoordinateManagerReturn => {
  const [modifiedCoordinates, setModifiedCoordinates] = useState<
    Map<string, Coordinate>
  >(new Map());

  const [originalCoordinates, setOriginalCoordinates] = useState<
    Map<string, Coordinate | null>
  >(new Map());

  const [aiExtractionFlags, setAiExtractionFlags] = useState<
    Map<string, boolean>
  >(new Map());

  const modifiedCoordinatesRef = useRef<Map<string, Coordinate>>(new Map());

  const flyerItemsRef = useRef<GfFlyerItemDto[]>(flyerItems);

  useEffect(() => {
    modifiedCoordinatesRef.current = modifiedCoordinates;
  }, [modifiedCoordinates]);

  useEffect(() => {
    flyerItemsRef.current = flyerItems;
  }, [flyerItems]);

  useEffect(() => {
    return () => {
      setModifiedCoordinates(new Map());
      setOriginalCoordinates(new Map());
      modifiedCoordinatesRef.current.clear();
    };
  }, []);

  /**
   * Validates and clamps coordinate values to ensure the box stays within image bounds.
   *
   * Ensures that coordinates and dimensions are positive, within the image boundaries,
   * and meet minimum size requirements. All values are rounded to integers.
   *
   * @param coordinate - The coordinate to validate
   * @returns Validated coordinate with clamped and rounded values
   */
  const validateCoordinateBounds = useCallback(
    (coordinate: Coordinate): Coordinate => {
      const { x, y, component } = coordinate;
      const { width, height } = component;

      const clampedX = Math.max(0, Math.min(x, imageBounds.width - width));
      const clampedY = Math.max(0, Math.min(y, imageBounds.height - height));
      const clampedWidth = Math.max(
        COORDINATE_BOX_CONSTANTS.MIN_SIZE,
        Math.min(width, imageBounds.width - clampedX),
      );
      const clampedHeight = Math.max(
        COORDINATE_BOX_CONSTANTS.MIN_SIZE,
        Math.min(height, imageBounds.height - clampedY),
      );

      return {
        ...coordinate,
        x: Math.round(clampedX),
        y: Math.round(clampedY),
        component: {
          ...component,
          width: Math.round(clampedWidth),
          height: Math.round(clampedHeight),
        },
      };
    },
    [imageBounds],
  );

  /**
   * Retrieves all flyer items that have coordinates on the current page.
   *
   * Checks both modified and original coordinates to determine which items belong
   * to the current page. Modified coordinates take precedence over original ones.
   * Uses refs to access latest state without triggering re-renders.
   *
   * @returns Array of flyer items on the current page
   */
  const getItemsForCurrentPage = useCallback(() => {
    const modCoords = modifiedCoordinatesRef.current;

    const itemsOnPage: GfFlyerItemDto[] = [];

    flyerItems.forEach((item) => {
      const modifiedCoord = modCoords.get(item.id!);

      if (modifiedCoord) {
        if (modifiedCoord.flyerPage === currentPage) {
          itemsOnPage.push(item);
        }
      } else if (item.coordinate && item.coordinate.flyerPage === currentPage) {
        itemsOnPage.push(item);
      }
    });

    return itemsOnPage;
  }, [flyerItems, currentPage]);

  /**
   * Updates the coordinate for a specific flyer item.
   *
   * Validates the new coordinate to ensure it stays within bounds, then stores both
   * the modified coordinate and the original (on first modification). Uses refs to
   * prevent stale closures. The original coordinate is deep cloned to prevent mutation.
   *
   * @param itemId - ID of the flyer item to update
   * @param newCoordinate - The new coordinate data
   */
  const updateCoordinate = useCallback(
    (itemId: string, newCoordinate: Coordinate, isAIExtraction?: boolean) => {
      const validatedCoordinate = validateCoordinateBounds(newCoordinate);

      setOriginalCoordinates((prev) => {
        if (!prev.has(itemId)) {
          const item = flyerItemsRef.current.find((i) => i.id === itemId);
          const newOriginals = new Map(prev);
          newOriginals.set(
            itemId,
            item?.coordinate
              ? JSON.parse(JSON.stringify(item.coordinate))
              : null,
          );
          return newOriginals;
        }
        return prev;
      });

      setModifiedCoordinates((prev) => {
        const newMap = new Map(prev);
        newMap.set(itemId, validatedCoordinate);
        modifiedCoordinatesRef.current = newMap;
        return newMap;
      });

      if (isAIExtraction !== undefined) {
        setAiExtractionFlags((prev) => {
          const newMap = new Map(prev);
          if (isAIExtraction) {
            newMap.set(itemId, true);
          } else {
            newMap.delete(itemId);
          }
          return newMap;
        });
      }
    },
    [validateCoordinateBounds],
  );

  /**
   * Retrieves the current coordinate for a flyer item.
   *
   * Returns the modified coordinate if one exists, otherwise returns the original
   * coordinate from the flyer item data.
   *
   * @param itemId - ID of the flyer item
   * @returns The coordinate data, or undefined if no coordinate exists
   */
  const getCoordinate = useCallback(
    (itemId: string): Coordinate | undefined => {
      const modified = modifiedCoordinates.get(itemId);
      if (modified) return modified;

      const item = flyerItems.find((i) => i.id === itemId);
      return item?.coordinate;
    },
    [modifiedCoordinates, flyerItems],
  );

  /**
   * Retrieves all modified coordinates with their original values.
   *
   * Returns an array of changes containing the flyer item, its original coordinate
   * (from before any modifications), and the new modified coordinate. Used to
   * determine what needs to be saved.
   *
   * @returns Array of coordinate changes ready for saving
   */
  const getModifiedCoordinates = useCallback((): CoordinateChange[] => {
    const changes: CoordinateChange[] = [];

    modifiedCoordinates.forEach((modifiedCoordinate, itemId) => {
      const flyerItem = flyerItems.find((item) => item.id === itemId);
      if (flyerItem) {
        const storedOriginal = originalCoordinates.get(itemId);
        const isAIExtraction = aiExtractionFlags.get(itemId) || false;
        changes.push({
          flyerItemId: itemId,
          flyerItem,
          originalCoordinate:
            storedOriginal !== undefined ? storedOriginal : null,
          modifiedCoordinate,
          isAIExtraction,
        });
      }
    });

    return changes;
  }, [modifiedCoordinates, flyerItems, originalCoordinates, aiExtractionFlags]);

  /**
   * Retrieves only coordinates marked for AI extraction.
   */
  const getAIExtractionCoordinates = useCallback((): CoordinateChange[] => {
    return getModifiedCoordinates().filter((change) => change.isAIExtraction);
  }, [getModifiedCoordinates]);

  /**
   * Retrieves only regular (non-AI) coordinate changes.
   */
  const getRegularCoordinates = useCallback((): CoordinateChange[] => {
    return getModifiedCoordinates().filter((change) => !change.isAIExtraction);
  }, [getModifiedCoordinates]);

  /**
   * Indicates whether there are unsaved coordinate changes.
   *
   * Returns true if any coordinates have been modified, false otherwise.
   */
  const isDirty = useMemo(() => {
    return modifiedCoordinates.size > 0;
  }, [modifiedCoordinates]);

  /**
   * Resets all coordinate changes, clearing both modified and original coordinate maps.
   * Also returns the list of AI extraction item IDs to be removed from the viewer.
   */
  const resetChanges = useCallback(() => {
    const aiExtractionItemIds: string[] = [];
    aiExtractionFlags.forEach((isAI, itemId) => {
      if (isAI) {
        aiExtractionItemIds.push(itemId);
      }
    });

    setModifiedCoordinates(new Map());
    setOriginalCoordinates(new Map());
    setAiExtractionFlags(new Map());

    return aiExtractionItemIds;
  }, [aiExtractionFlags]);

  /**
   * Resets the coordinate changes for a specific flyer item.
   *
   * Removes the item from both modified and original coordinate maps, effectively
   * reverting it back to its original state.
   *
   * @param itemId - ID of the flyer item to reset
   */
  const resetCoordinate = useCallback((itemId: string) => {
    setModifiedCoordinates((prev) => {
      const newMap = new Map(prev);
      newMap.delete(itemId);
      modifiedCoordinatesRef.current = newMap;
      return newMap;
    });
    setOriginalCoordinates((prev) => {
      const newMap = new Map(prev);
      newMap.delete(itemId);
      return newMap;
    });
    setAiExtractionFlags((prev) => {
      const newMap = new Map(prev);
      newMap.delete(itemId);
      return newMap;
    });
  }, []);

  /**
   * Check if an item is marked as AI extraction
   */
  const isAIExtraction = useCallback(
    (itemId: string): boolean => {
      return aiExtractionFlags.get(itemId) || false;
    },
    [aiExtractionFlags],
  );

  return {
    getItemsForCurrentPage,
    updateCoordinate,
    getCoordinate,
    getModifiedCoordinates,
    getAIExtractionCoordinates,
    getRegularCoordinates,
    isAIExtraction,
    isDirty,
    resetChanges,
    resetCoordinate,
  };
};
